<template>
  <div v-if="isVisible" class="prohor-widget">
    <transition name="prohor-intro">
      <div
          v-if="showIntro"
          class="prohor-widget__intro"
          role="dialog"
          aria-label="Знакомство с Прохором"
      >
        <div class="prohor-widget__greeting-card">
          <button
              type="button"
              class="prohor-widget__greeting-close"
              aria-label="Закрыть"
              @click="closeChat"
          >
            <CloseOutlined />
          </button>
          <div class="prohor-widget__greeting-head">
            <div class="prohor-widget__greeting-avatar">
              <img :src="prohorIcon" alt="Прохор" />
            </div>
            <span class="prohor-widget__greeting-name">Прохор, ИИ-помощник</span>
          </div>
          <p class="prohor-widget__greeting-text">
            Здравствуйте!<br />
            Меня зовут Прохор. Я — ИИ-помощник: помогу с навигацией по кабинету и отвечу на ваши вопросы.
          </p>
          <p
              v-if="pageHintAvailable"
              class="prohor-widget__greeting-text prohor-widget__greeting-text--accent"
          >
            Могу помочь на этой странице — подсказать, что и как тут можно сделать.
          </p>
        </div>

        <div class="prohor-widget__greeting-actions">
          <button
              v-if="pageHintAvailable"
              type="button"
              class="prohor-widget__greeting-action prohor-widget__greeting-action--primary"
              @click="onPageHelpQuestion"
          >
            Что я могу сделать на этой странице?
          </button>
          <button
              v-for="action in emptyStateActions"
              :key="action"
              type="button"
              class="prohor-widget__greeting-action"
              @click="onEmptyAction(action)"
          >
            {{ action }}
          </button>
        </div>
      </div>
    </transition>

    <transition name="prohor-panel">
      <div
          v-if="showChatPanel"
          class="prohor-widget__panel"
          :class="{
            'prohor-widget__panel--mobile': isMobile,
            'prohor-widget__panel--split': isSplitMode && !isMobile,
            'prohor-widget__panel--resizing': isResizing,
          }"
          :style="panelStyle"
          role="dialog"
          aria-label="Чат с Прохором"
      >
        <header class="prohor-widget__header">
          <span class="prohor-widget__header-title">Прохор</span>
          <div class="prohor-widget__header-actions">
            <button
                v-if="canUseSplitMode && !isMobile"
                type="button"
                class="prohor-widget__header-btn prohor-widget__header-btn--panel"
                :class="{ 'prohor-widget__header-btn--active': isSplitMode }"
                :aria-label="isSplitMode ? 'Выключить split-режим' : 'Включить split-режим'"
                @click="toggleSplitMode"
            >
              <span class="prohor-widget__panel-icon" aria-hidden="true" />
            </button>
            <button
                type="button"
                class="prohor-widget__header-btn"
                aria-label="Свернуть чат"
                @click="closeChat"
            >
              <DownOutlined />
            </button>
          </div>
        </header>

        <div ref="messagesRef" class="prohor-widget__messages">
          <prohor-message
              v-for="message in messages"
              :key="message.id"
              :message="message"
              @suggestion="onSuggestion"
              @action="onMessageAction"
          />
        </div>

        <footer class="prohor-widget__composer">
          <div class="prohor-widget__composer-field">
            <a-textarea
                :key="inputKey"
                v-model:value="draft"
                :bordered="false"
                :rows="2"
                :maxlength="1000"
                placeholder="Сообщение..."
                class="prohor-widget__input"
                :disabled="loading"
                @keydown="onKeyDown"
            />
            <div class="prohor-widget__composer-toolbar">
              <button
                  type="button"
                  class="prohor-widget__toolbar-btn"
                  aria-label="Прикрепить файл"
                  disabled
                  title="Скоро"
              >
                <PaperClipOutlined />
              </button>
              <button
                  type="button"
                  class="prohor-widget__toolbar-btn prohor-widget__toolbar-btn--send"
                  :disabled="!isDraftValid || loading"
                  aria-label="Отправить"
                  @click="send"
              >
                <SendOutlined />
              </button>
            </div>
          </div>
        </footer>

        <div
            v-if="!isMobile && !isSplitMode"
            class="prohor-widget__resize-handle"
            @mousedown="startResize"
        />
      </div>
    </transition>

    <button
        v-if="!isWidgetOpen"
        type="button"
        class="prohor-widget__fab"
        aria-label="Открыть чат с Прохором"
        @click="openWidget"
    />
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { CloseOutlined, DownOutlined, PaperClipOutlined, SendOutlined } from "@ant-design/icons-vue";
import ProhorMessage from "@/components/Prohor/ProhorMessage.vue";
import prohorIcon from "@/assets/images/icons/prohor_ai.svg";
import { hasPageHint } from "@/store/prohor/prohorPageHints";

const EMPTY_STATE_ACTIONS = ["Что умеет помощник", "База знаний"];

const QUICK_REPLY_ACTIONS = new Set(EMPTY_STATE_ACTIONS);

export default {
  name: "ProhorWidget",
  components: {
    ProhorMessage,
    CloseOutlined,
    DownOutlined,
    PaperClipOutlined,
    SendOutlined,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const draft = ref("");
    const inputKey = ref(0);
    const messagesRef = ref(null);
    const isMobile = ref(false);
    const canUseSplitMode = ref(false);
    const isSplitMode = ref(false);
    const panelWidth = ref(400);
    const panelHeight = ref(600);
    const isResizing = ref(false);
    let resizeState = null;

    const profile = computed(() => store.state.profile.profile);
    const role = computed(() => profile.value?.role ?? null);
    const hasSubscription = computed(() => {
      const subscriptions = store.state.subscription?.permittedSubscriptions;
      return Array.isArray(subscriptions) && subscriptions.length > 0;
    });
    const chatOpen = computed(() => store.state.ai.chatOpen);
    const messages = computed(() => store.state.ai.messages);
    const loading = computed(() => store.state.ai.loading);

    const currentPath = computed(() => route?.path ?? "/");
    const pageHintAvailable = computed(() => hasPageHint(currentPath.value));

    const hasMessages = computed(() => messages.value.length > 0);
    const isWidgetOpen = computed(() => chatOpen.value);
    const showIntro = computed(() => chatOpen.value && !hasMessages.value);
    const showChatPanel = computed(() => chatOpen.value && hasMessages.value);

    const isVisible = computed(
        () => !!profile.value && !!profile.value.role
    );

    const isDraftValid = computed(() => {
      const trimmed = draft.value.trim();
      return trimmed.length >= 2 && trimmed.length <= 1000;
    });

    const clearDraft = () => {
      draft.value = "";
      inputKey.value += 1;
    };

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 759;
      canUseSplitMode.value = window.innerWidth >= 1720;

      if (!canUseSplitMode.value && isSplitMode.value) {
        isSplitMode.value = false;
        document.body.classList.remove("prohor-split-active");
      }
    };

    const updateSplitLayout = () => {
      if (isSplitMode.value && !isMobile.value) {
        document.body.style.setProperty("--prohor-split-width", `${panelWidth.value}px`);
        document.body.classList.add("prohor-split-active");
      } else {
        document.body.classList.remove("prohor-split-active");
      }
    };

    onMounted(() => {
      handleResize();
      window.addEventListener("resize", handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
      document.body.classList.remove("prohor-split-active");
      document.body.classList.remove("prohor-resizing-active");
      document.body.style.removeProperty("--prohor-split-width");
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", stopResize);
    });
    const panelStyle = computed(() => {
      if (isMobile.value) return null;
      if (isSplitMode.value) {
        return {
          width: `${panelWidth.value}px`,
        };
      }
      return {
        width: `${panelWidth.value}px`,
        height: `${panelHeight.value}px`,
      };
    });

    const toggleSplitMode = () => {
      if (!canUseSplitMode.value || isMobile.value) return;
      isSplitMode.value = !isSplitMode.value;
      updateSplitLayout();
    };

    const onResizeMove = (event) => {
      if (!isResizing.value || !resizeState) return;
      event.preventDefault();

      const deltaX = event.clientX - resizeState.startX;
      const deltaY = event.clientY - resizeState.startY;

      const maxWidth = Math.max(420, window.innerWidth - 48);
      const maxHeight = Math.max(480, window.innerHeight - 48);

      panelWidth.value = Math.min(
          maxWidth,
          Math.max(360, resizeState.startWidth - deltaX)
      );
      panelHeight.value = Math.min(
          maxHeight,
          Math.max(480, resizeState.startHeight - deltaY)
      );
    };

    const stopResize = () => {
      if (!isResizing.value) return;
      isResizing.value = false;
      resizeState = null;
      document.body.classList.remove("prohor-resizing-active");
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", stopResize);
    };

    const startResize = (event) => {
      if (isMobile.value || isSplitMode.value) return;
      event.preventDefault();
      event.stopPropagation();
      isResizing.value = true;
      document.body.classList.add("prohor-resizing-active");
      resizeState = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: panelWidth.value,
        startHeight: panelHeight.value,
      };
      window.addEventListener("mousemove", onResizeMove);
      window.addEventListener("mouseup", stopResize);
    };


    const scrollToBottom = () => {
      const container = messagesRef.value;
      if (!container) return;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    };

    const focusInput = () => {
      nextTick(() => {
        const textarea = document.querySelector(
            ".prohor-widget__panel .prohor-widget__input textarea"
        );
        textarea?.focus();
      });
    };

    const openWidget = () => {
      store.dispatch("ai/openChat");
    };

    const closeChat = () => {
      clearDraft();
      store.dispatch("ai/closeChat");
    };

    const resetChat = () => {
      clearDraft();
      store.dispatch("ai/resetChat");
    };

    const send = async () => {
      if (!isDraftValid.value || loading.value) return;
      const question = draft.value.trim();
      clearDraft();
      await store.dispatch("ai/askQuestion", {
        question,
        role: role.value,
        path: currentPath.value,
        hasSubscription: hasSubscription.value,
      });
      await nextTick();
      scrollToBottom();
      focusInput();
    };

    const sendQuestion = async (question) => {
      if (loading.value) return;
      clearDraft();
      await store.dispatch("ai/askQuestion", {
        question,
        role: role.value,
        path: currentPath.value,
        hasSubscription: hasSubscription.value,
      });
      await nextTick();
      scrollToBottom();
      focusInput();
    };

    const onPageHelpQuestion = async () => {
      await sendQuestion("Что я могу сделать на этой странице?");
    };

    const onMessageAction = (action) => {
      if (!action) return;
      if (action.url) {
        window.open(action.url, "_blank", "noopener,noreferrer");
        return;
      }
      if (!action.route) return;
      router.push(action.route).catch(() => {});
    };

    const onKeyDown = (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    };

    const onSuggestion = (text) => {
      sendQuestion(text);
    };

    const onEmptyAction = async (action) => {
      if (!QUICK_REPLY_ACTIONS.has(action)) return;

      clearDraft();
      await store.dispatch("ai/sendQuickReply", action);
      await nextTick();
      scrollToBottom();
      focusInput();
    };

    watch(showChatPanel, (visible) => {
      if (visible) {
        nextTick(() => {
          scrollToBottom();
          focusInput();
        });
      }
    });

    watch(
        () => messages.value.length,
        () => {
          if (showChatPanel.value) {
            nextTick(scrollToBottom);
          }
        }
    );

    watch(chatOpen, (isOpen) => {
      if (isOpen && hasMessages.value) {
        store.dispatch("ai/sendPageHelpPrompt", {
          path: currentPath.value,
        });
      }

      if (!isOpen) {
        clearDraft();
        isSplitMode.value = false;
        updateSplitLayout();
      }
    });

    watch([isSplitMode, panelWidth, isMobile], () => {
      updateSplitLayout();
    });

    return {
      draft,
      inputKey,
      messagesRef,
      isMobile,
      canUseSplitMode,
      isSplitMode,
      panelStyle,
      isVisible,
      isWidgetOpen,
      showIntro,
      showChatPanel,
      chatOpen,
      messages,
      loading,
      isDraftValid,
      pageHintAvailable,
      prohorIcon,
      emptyStateActions: EMPTY_STATE_ACTIONS,
      openWidget,
      closeChat,
      resetChat,
      toggleSplitMode,
      startResize,
      send,
      onKeyDown,
      onSuggestion,
      onEmptyAction,
      onPageHelpQuestion,
      onMessageAction,
    };
  },
};
</script>

<style scoped lang="scss">
.prohor-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 999;
  font-family: "Open Sans", sans-serif;

  @media (max-width: $mobile-breakpoint) {
    right: 0;
    bottom: 0;
    left: 0;
    top: 0;
    pointer-events: none;
  }
}

.prohor-widget__fab {
  position: relative;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  background-image: url("../../assets/images/icons/prohor_float_button.svg");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.08),
    0 3px 6px rgba(0, 0, 0, 0.12),
    0 9px 28px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  margin-left: auto;
  pointer-events: auto;
  flex-shrink: 0;

  @media (max-width: $mobile-breakpoint) {
    position: fixed;
    right: 16px;
    bottom: 16px;
  }
}

.prohor-widget__intro {
  position: absolute;
  right: 0;
  bottom: 52px;
  width: 320px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: auto;

  @media (max-width: $mobile-breakpoint) {
    position: fixed;
    right: 16px;
    bottom: 72px;
    left: 16px;
    width: auto;
    max-width: none;
  }
}

.prohor-widget__panel {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 400px;
  height: 600px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border-grey);
  pointer-events: auto;

  &--split {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    height: 100vh !important;
    max-height: 100vh;
    border-radius: 0;
    border-top: none;
    border-right: none;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
    z-index: 1101;
  }

  &--mobile {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    border: none;
    z-index: 1101;
  }
}

.prohor-widget__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid var(--border-grey);
}

.prohor-widget__header-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: var(--main-font);
}

.prohor-widget__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.prohor-widget__header-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--neutral-black-font);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--neutral-grey);
    color: var(--main-font);
  }

  &--panel {
    cursor: pointer;

    &:hover {
      background: var(--neutral-grey);
    }
  }

  &--active {
    color: var(--main-green);
    background: var(--focus-color);
  }
}

.prohor-widget__panel-icon {
  display: block;
  width: 16px;
  height: 16px;
  background-color: currentColor;
  mask: url("../../assets/images/icons/prohor_chat_panel_icon.svg") center / contain no-repeat;
  -webkit-mask: url("../../assets/images/icons/prohor_chat_panel_icon.svg") center / contain no-repeat;
}

.prohor-widget__messages {
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-grey);
    border-radius: 3px;
  }
}

.prohor-widget__greeting-card {
  position: relative;
  background: #fff;
  border: 1px solid var(--border-grey);
  border-radius: 8px;
  padding: 14px 14px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.prohor-widget__greeting-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--neutral-text-description);
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--main-font);
  }
}

.prohor-widget__greeting-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding-right: 20px;
}

.prohor-widget__greeting-avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--main-green);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 14px;
  }
}

.prohor-widget__greeting-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font);
}

.prohor-widget__greeting-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--main-font);
  text-align: left;

  & + & {
    margin-top: 8px;
  }

  &--accent {
    color: var(--main-green);
  }
}

.prohor-widget__greeting-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.prohor-widget__greeting-action {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--main-green);
  background: #fff;
  color: var(--main-green);
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--focus-color);
  }

  &--primary {
    background: var(--main-green);
    color: #fff;

    &:hover {
      background: #fff;
      color: var(--main-green);
    }
  }
}

.prohor-widget__composer {
  flex-shrink: 0;
  padding: 12px 16px 16px;
  background: #fff;
}

.prohor-widget__composer-field {
  position: relative;
  width: 100%;
  height: 109px;
  border: 1px solid var(--border-grey);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.prohor-widget__input {
  width: 100%;
  height: 100%;
}

:deep(.prohor-widget__input.ant-input) {
  width: 100% !important;
  height: calc(100% - 44px) !important;
  min-height: unset !important;
  max-height: unset !important;
  resize: none !important;
  padding: 12px 14px 4px !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  font-size: 14px !important;
  line-height: 20px !important;

  &::placeholder {
    color: var(--neutral-text-description);
  }

  &:focus {
    box-shadow: none !important;
  }
}

.prohor-widget__composer-toolbar {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.prohor-widget__toolbar-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: var(--neutral-grey);
  color: var(--neutral-black-font);
  font-size: 16px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--border-grey);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--send {
    color: var(--main-font);

    &:not(:disabled):hover {
      background: var(--focus-color);
      color: var(--main-green);
    }
  }
}

.prohor-widget__resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 4px;
    width: 8px;
    height: 8px;
    border-left: 2px solid var(--border-grey);
    border-top: 2px solid var(--border-grey);
    border-radius: 2px 0 0 0;
  }
}

:global(body.prohor-split-active) {
  padding-right: var(--prohor-split-width, 400px);
  transition: padding-right 0.18s ease;
}

:global(body.prohor-resizing-active) {
  user-select: none;
  -webkit-user-select: none;
  cursor: nwse-resize;
}

.prohor-widget__panel--resizing {
  * {
    user-select: none;
  }
}

.prohor-intro-enter-active,
.prohor-intro-leave-active,
.prohor-panel-enter-active,
.prohor-panel-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
  transform-origin: bottom right;
}

.prohor-intro-enter-from,
.prohor-intro-leave-to,
.prohor-panel-enter-from,
.prohor-panel-leave-to {
  transform: translateY(12px);
  opacity: 0;
}

@media (max-width: $mobile-breakpoint) {
  .prohor-panel-enter-from,
  .prohor-panel-leave-to {
    transform: translateY(100%);
  }
}
</style>
